import React from 'react';
import Modal from './Modal';
import { formatDate } from '../utils/dateFormatter';

interface TermsOfServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TermsOfServiceModal: React.FC<TermsOfServiceModalProps> = ({ isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} title="Terms of Service" onClose={onClose}>
      <div className="terms-modal-content">
        <div className="terms-text">
          <p><strong>Effective Date: {formatDate(new Date())}</strong></p>
          
          <h3>1. Acceptance of Terms</h3>
          <p>By accessing and using LIFE PSENsOR ("the Platform", "our Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.</p>

          <h3>2. Medical Disclaimer and Limitation of Liability</h3>
          
          <h4>2.1 AI Chatbot Information and General Wellness Advice</h4>
          <p><strong>CRITICAL NOTICE:</strong> The AI chatbots can provide general health knowledge, educational information, and wellness advice based on established health principles. However, they are NOT a substitute for professional medical advice, diagnosis, or treatment from qualified healthcare providers.</p>
          
          <p><strong>What AI chatbots CAN provide:</strong></p>
          <ul>
            <li>General health education and information</li>
            <li>Wellness tips and lifestyle advice</li>
            <li>General knowledge about health conditions</li>
            <li>Educational content about nutrition, fitness, and mental health</li>
          </ul>

          <p><strong>What AI chatbots CANNOT provide:</strong></p>
          <ul>
            <li>Medical diagnosis or treatment recommendations</li>
            <li>Prescription advice or medication recommendations</li>
            <li>Specific medical interventions</li>
            <li>Professional medical consultation</li>
          </ul>

          <h4>2.2 No Doctor-Patient Relationship</h4>
          <ul>
            <li>No doctor-patient relationship is established through the use of this Platform</li>
            <li>The AI chatbots are not licensed medical professionals</li>
            <li>Information provided by AI chatbots should not be considered as medical advice from a qualified healthcare provider</li>
          </ul>

          <h4>2.3 User Responsibility for Wellness Decisions</h4>
          <ul>
            <li>Users are encouraged to use AI chatbot information for general health education and wellness guidance</li>
            <li>For medical treatment, diagnosis, or specific health concerns, users must consult with qualified healthcare professionals</li>
            <li>Users should not delay seeking professional medical advice when needed</li>
            <li>AI chatbot information should complement, not replace, professional medical consultation</li>
            <li>Users must not rely solely on AI chatbot information for medical treatment decisions</li>
          </ul>

          <h4>2.4 Emergency Situations</h4>
          <ul>
            <li>In case of medical emergency, users must contact emergency services immediately</li>
            <li>Do not use this Platform for emergency medical situations</li>
            <li>Call 911 or your local emergency number for urgent medical needs</li>
          </ul>

          <h3>3. Platform Limitations and Disclaimers</h3>
          
          <h4>3.1 AI Technology Limitations</h4>
          <ul>
            <li>AI responses may contain errors, inaccuracies, or outdated information</li>
            <li>AI technology has inherent limitations and may not always provide accurate health information</li>
            <li>The Platform does not guarantee the accuracy, completeness, or reliability of AI-generated content</li>
            <li>AI responses are based on general information and may not be applicable to individual circumstances</li>
          </ul>

          <h3>4. User Responsibilities and Prohibited Uses</h3>
          
          <h4>4.1 User Responsibilities</h4>
          <ul>
            <li>Provide accurate information when using assessments</li>
            <li>Consult qualified healthcare professionals for medical advice</li>
            <li>Use the Platform responsibly and in accordance with these Terms</li>
            <li>Report any technical issues or concerns to our support team</li>
          </ul>

          <h4>4.2 Prohibited Uses</h4>
          <p>Users are prohibited from:</p>
          <ul>
            <li>Using the Platform for emergency medical situations</li>
            <li>Relying solely on AI responses for medical treatment decisions</li>
            <li>Sharing AI responses as professional medical advice or diagnosis</li>
            <li>Using the Platform to diagnose or treat specific medical conditions</li>
            <li>Attempting to establish a doctor-patient relationship through the Platform</li>
            <li>Using AI responses as a substitute for professional medical consultation when medical treatment is needed</li>
          </ul>

          <h3>5. Limitation of Liability</h3>
          <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
          <ul>
            <li>The Platform, its owners, operators, and affiliates shall not be liable for any direct, indirect, incidental, special, or consequential damages</li>
            <li>We are not responsible for any health outcomes, medical decisions, or consequences resulting from use of the Platform</li>
            <li>Our total liability shall not exceed the amount paid by the user for the Service (if any)</li>
            <li>We disclaim all warranties, express or implied, including but not limited to warranties of merchantability and fitness for a particular purpose</li>
          </ul>

          <h3>6. Privacy and Data Protection</h3>
          <ul>
            <li>We take reasonable measures to protect user wellness information</li>
            <li>Wellness data is stored securely and used only for Platform functionality</li>
            <li>Users retain ownership of their wellness information</li>
            <li>We do not sell personal wellness information to third parties</li>
          </ul>

          <h3>7. Contact Information</h3>
          <p>For questions about these Terms or the Platform, please contact us at support@lifepsensor.com</p>

          <div className="terms-footer">
            <p><strong>IMPORTANT REMINDER:</strong> This Platform provides general health education, wellness advice, and health knowledge. For medical diagnosis, treatment, or specific health concerns, always consult qualified healthcare professionals. In case of medical emergency, contact emergency services immediately.</p>
            
            <p><strong>By using LIFE PSENsOR, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.</strong></p>
          </div>
        </div>
        
        <div className="terms-modal-actions">
          <button className="btn btn-primary" onClick={onClose}>
            I Understand
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default TermsOfServiceModal;

